import { useEffect, useRef, useState } from 'react';
import { fetchPresentRuntime } from '../../api/modules/components';
import type { PresentComponentMeta, PresentRuntimePayload } from '../../api/modules/components';
import { LOGICAL_W } from './demo-utils';

declare global {
  interface Window {
    __PLAYGEN_PRESENT?: any;
    __PLAYGEN_UI?: Record<string, any>;
    __PLAYGEN_PRESENT_PLAN?: any;
    __pgPresentEmit?: (name: string, payload?: any) => any;
    showGameHint?: (text: string, options?: any) => any;
  }
}

interface Props {
  component: PresentComponentMeta | null; // null = 总线总览
}

/** present runtime 只需 eval 一次（内部有重复守卫）；重置时删除 key 重新注入 */
function injectRuntime(source: string): void {
  delete window.__PLAYGEN_PRESENT;
  delete window.__PLAYGEN_PRESENT_PLAN;
  const fn = new Function(source);
  fn();
}

/** present 活预览：真实 eval 呈现总线，按选中组件的 triggers 触发事件并观察反馈 */
export function PresentPreview({ component }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [payload, setPayload] = useState<PresentRuntimePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState('');
  const [matched, setMatched] = useState<string[]>([]);
  const [uiMirror, setUiMirror] = useState<Record<string, any>>({});

  useEffect(() => {
    let alive = true;
    fetchPresentRuntime()
      .then((p) => {
        if (!alive) return;
        injectRuntime(p.source);
        setPayload(p);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => { alive = false; };
  }, []);

  // rAF：持续绘制 drawHint，并轮询 __PLAYGEN_UI 镜像与 lastEvent
  useEffect(() => {
    if (!payload) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = LOGICAL_W * dpr;
    canvas.height = 220 * dpr;
    let raf = 0;
    const loop = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, LOGICAL_W, 220);
      window.__PLAYGEN_PRESENT?.drawHint?.(ctx, LOGICAL_W, 220);
      const le = window.__PLAYGEN_PRESENT?.state?.lastEvent;
      setLastEvent(le ? `${le.name} ${JSON.stringify(le.payload)}` : '（尚未触发事件）');
      setUiMirror({ ...(window.__PLAYGEN_UI || {}) });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [payload]);

  const emit = (name: string) => {
    const defs = window.__pgPresentEmit?.(name, { demo: true }) || [];
    setMatched(defs.map((d: any) => d.id));
  };

  const hint = () => {
    window.showGameHint?.('向左滑动消除同类方块', { durationMs: 1800 });
  };

  const triggers = component?.triggers?.length ? component.triggers : (payload?.events || []);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative rounded-xl border border-slate-700/60 bg-[#0d1220] overflow-hidden" style={{ width: LOGICAL_W, height: 220 }}>
        <canvas ref={canvasRef} style={{ width: LOGICAL_W, height: 220 }} />
        {error && <div className="absolute inset-0 flex items-center justify-center text-rose-400 text-sm">{error}</div>}
        {!payload && !error && <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">注入呈现总线…</div>}
      </div>

      <div className="text-xs text-slate-400 text-center max-w-[340px]">
        {component
          ? <>组件 <span className="text-cyan-300 font-mono">{component.id}</span>（{component.kind}）监听以下触发事件</>
          : '呈现总线全事件触发'}
      </div>

      <div className="flex flex-wrap gap-2 justify-center max-w-[360px]">
        {triggers.map((t) => (
          <button key={t} type="button" onClick={() => emit(t)} className="glow-btn px-3 py-1.5 text-xs rounded-lg font-mono">
            {t}
          </button>
        ))}
        <button type="button" onClick={hint} className="glow-btn px-3 py-1.5 text-xs rounded-lg">
          中途提示
        </button>
      </div>

      <div className="w-full max-w-[360px] rounded-lg border border-slate-700/60 bg-slate-900/60 px-3 py-2 space-y-1.5">
        <div className="text-xs">
          <span className="text-slate-500">lastEvent：</span>
          <span className="text-cyan-300 font-mono break-all">{lastEvent}</span>
        </div>
        <div className="text-xs">
          <span className="text-slate-500">emit 命中组件：</span>
          <span className="text-fuchsia-300 font-mono">{matched.length ? matched.join(', ') : '无'}</span>
        </div>
        <div className="text-xs">
          <span className="text-slate-500">__PLAYGEN_UI 镜像：</span>
          <pre className="mt-1 max-h-28 overflow-auto text-[10px] leading-4 text-slate-400 font-mono whitespace-pre-wrap">
            {Object.keys(uiMirror).length ? JSON.stringify(uiMirror, null, 1) : '（空）'}
          </pre>
        </div>
      </div>
    </div>
  );
}
